(function () {
  "use strict";

  /* FIXED_PIN_V2 + DOUBLE_BUFFER_BLIT — mobile Safari/Chrome scrub stability */

  function initRoot(root) {
    if (!root || root.getAttribute("data-c10k-init") === "1") return;
    root.setAttribute("data-c10k-init", "1");

    var VIDEO_BYTES = parseInt(root.getAttribute("data-video-bytes") || "3510122", 10);
    var VIDEO_URL = root.getAttribute("data-video-url") || "";
    var ENDING_POSTER = root.getAttribute("data-ending-url") || "";
    var POSTER_URL = root.getAttribute("data-poster-url") || "";
    var STREAM_THRESHOLD = 8 * 1024 * 1024;

    var pin = root.querySelector("[data-c10k-pin]");
    var stage = root.querySelector(".c10k-scrub__stage");
    var media = root.querySelector("[data-c10k-media]");
    var video = root.querySelector("[data-c10k-video]");
    var still = root.querySelector("[data-c10k-still]");
    var captions = Array.prototype.slice.call(root.querySelectorAll("[data-c10k-band]"));
    var scrollHint = root.querySelector("[data-c10k-hint]");
    var loadRing = root.querySelector("[data-c10k-ring]");

    if (!pin || !media || !video || !still) return;

    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var isMobile = window.matchMedia("(max-width: 768px)").matches;
    /* Mobile: canvas blit + seek queue (Safari + Chrome both flicker on direct video scrub). */
    var useCanvas = isMobile && !reduceMotion;
    /* Mobile: JS fixed-pin replaces CSS sticky (sticky + mobile chrome = shiver). */
    var useFixedPin = isMobile && !reduceMotion;
    var useStatic = reduceMotion;

    var targetProgress = 0;
    var displayTime = 0;
    var duration = 0;
    var seeking = false;
    var pendingTime = null;
    var lastSeekAt = 0;
    var ready = false;
    var hasGoodFrame = false;
    var rafId = 0;
    var idle = true;
    /* Mobile: denser stepped scrub — smoother than 0.14/90ms, still single-seek + blit-on-seeked */
    var convergedEps = isMobile ? 0.028 : 0.012;
    var seekEps = isMobile ? 0.09 : 0.04; /* ~0.09s ≈ 2 frames @24fps */
    var seekMinMs = isMobile ? 52 : 0;

    /* Lock layout vh once (visualViewport preferred). Do not chase chrome show/hide. */
    function measureVh() {
      if (window.visualViewport && window.visualViewport.height) {
        return Math.max(1, Math.round(window.visualViewport.height));
      }
      return Math.max(1, Math.round(window.innerHeight || 1));
    }
    var layoutVh = measureVh();
    var pinMode = ""; /* before | fixed | after */

    var canvas = null;
    var ctx = null;
    var backCanvas = null;
    var backCtx = null;
    var canvasW = 0;
    var canvasH = 0;

    function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

    function easePlateau(p) {
      var holds = [
        [0.00, 0.18],
        [0.32, 0.44],
        [0.56, 0.68],
        [0.82, 0.96]
      ];
      for (var i = 0; i < holds.length; i++) {
        var a = holds[i][0], b = holds[i][1];
        if (p >= a && p <= b) {
          var mid = (a + b) * 0.5;
          var t = (p - a) / (b - a || 1);
          var s = t * t * (3 - 2 * t);
          return a + (mid - a) * (1 - Math.abs(s * 2 - 1) * 0.35) + (b - a) * s * 0.5;
        }
      }
      return p;
    }

    function applyStagePx() {
      if (!stage || !isMobile) return;
      stage.style.setProperty("height", layoutVh + "px", "important");
    }

    /* FIXED_PIN_V2: classic scrub pin — fixed while pin overlaps viewport */
    function updateFixedPin() {
      if (!useFixedPin || !stage || !pin || useStatic) return;
      var rect = pin.getBoundingClientRect();
      var next;
      if (rect.top <= 0 && rect.bottom > layoutVh) {
        next = "fixed";
      } else if (rect.top > 0) {
        next = "before";
      } else {
        next = "after";
      }
      if (next === pinMode) return;
      pinMode = next;

      stage.style.setProperty("left", "0", "important");
      stage.style.setProperty("right", "0", "important");
      stage.style.setProperty("width", "100%", "important");
      stage.style.setProperty("height", layoutVh + "px", "important");

      if (next === "fixed") {
        stage.style.setProperty("position", "fixed", "important");
        stage.style.setProperty("top", "0", "important");
        stage.style.setProperty("bottom", "auto", "important");
        stage.style.setProperty("z-index", "2", "important");
        stage.classList.add("is-fixed-pin");
        pin.classList.add("is-pinning");
      } else if (next === "before") {
        stage.style.setProperty("position", "absolute", "important");
        stage.style.setProperty("top", "0", "important");
        stage.style.setProperty("bottom", "auto", "important");
        stage.style.setProperty("z-index", "1", "important");
        stage.classList.remove("is-fixed-pin");
        pin.classList.remove("is-pinning");
      } else {
        stage.style.setProperty("position", "absolute", "important");
        stage.style.setProperty("top", "auto", "important");
        stage.style.setProperty("bottom", "0", "important");
        stage.style.setProperty("z-index", "1", "important");
        stage.classList.remove("is-fixed-pin");
        pin.classList.remove("is-pinning");
      }
    }

    function clearFixedPinStyles() {
      if (!stage) return;
      pinMode = "";
      stage.classList.remove("is-fixed-pin");
      if (pin) pin.classList.remove("is-pinning");
      [
        "position", "top", "bottom", "left", "right",
        "width", "height", "z-index"
      ].forEach(function (prop) {
        stage.style.removeProperty(prop);
      });
    }

    function ensureCanvas() {
      if (!useCanvas || canvas) return;
      canvas = document.createElement("canvas");
      canvas.className = "c10k-scrub__canvas";
      canvas.setAttribute("data-c10k-canvas", "");
      canvas.setAttribute("aria-hidden", "true");
      media.appendChild(canvas);
      ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
      media.classList.add("is-canvas");
      video.setAttribute("aria-hidden", "true");
      /* Video fully off compositor — canvas is what the user sees */
      video.style.cssText =
        "opacity:0;pointer-events:none;position:absolute;width:1px;height:1px;left:-9999px;top:0;visibility:hidden;";
      resizeCanvas();
    }

    function resizeCanvas() {
      if (!canvas || !media) return;
      var rect = media.getBoundingClientRect();
      var cssW = Math.max(1, Math.round(rect.width || (window.innerWidth || 1)));
      var cssH = Math.max(1, Math.round(rect.height || layoutVh));
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(1, Math.round(cssW * dpr));
      var h = Math.max(1, Math.round(cssH * dpr));
      if (w === canvasW && h === canvasH) return;
      canvasW = w;
      canvasH = h;
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = cssW + "px";
      canvas.style.height = cssH + "px";
      if (backCanvas) {
        backCanvas.width = w;
        backCanvas.height = h;
      }
      /* Re-blit last good frame into new buffer if available */
      if (hasGoodFrame && !seeking) blitFrame();
    }

    /* DOUBLE_BUFFER_BLIT: draw to back canvas; copy to visible only after good frame.
       NEVER fillRect the visible canvas before a successful drawImage. */
    function blitFrame() {
      if (!useCanvas || !ctx || !canvas || !video) return;
      if (seeking) return; /* mid-seek frames are blank/glitchy — keep prior pixels */
      var vw = video.videoWidth;
      var vh = video.videoHeight;
      if (!vw || !vh || canvasW < 1 || canvasH < 1) return;

      if (!backCanvas || backCanvas.width !== canvasW || backCanvas.height !== canvasH) {
        backCanvas = document.createElement("canvas");
        backCanvas.width = canvasW;
        backCanvas.height = canvasH;
        backCtx = backCanvas.getContext("2d", { alpha: false });
      }
      if (!backCtx) return;

      try {
        var scale = Math.max(canvasW / vw, canvasH / vh);
        var dw = vw * scale;
        var dh = vh * scale;
        var dx = (canvasW - dw) * 0.5;
        var dy = (canvasH - dh) * 0.5;
        backCtx.fillStyle = "#0A0A0A";
        backCtx.fillRect(0, 0, canvasW, canvasH);
        backCtx.drawImage(video, dx, dy, dw, dh);
        /* Only now copy a complete frame to the visible canvas */
        ctx.drawImage(backCanvas, 0, 0);
        hasGoodFrame = true;
        if (!media.classList.contains("is-ready")) {
          media.classList.add("is-ready");
          media.setAttribute("data-state", "ready");
        }
      } catch (e) {
        /* drawImage can throw if frame not ready — keep prior visible pixels */
      }
    }

    function scrollProgress() {
      if (useStatic) return 0;
      var rect = pin.getBoundingClientRect();
      var total = pin.offsetHeight - layoutVh;
      if (total <= 0) return 0;
      var scrolled = -rect.top;
      return clamp(scrolled / total, 0, 1);
    }

    function updateCaptions(p) {
      for (var i = 0; i < captions.length; i++) {
        var raw = captions[i].getAttribute("data-range") || "0,1";
        var parts = raw.split(",");
        var a = parseFloat(parts[0]);
        var b = parseFloat(parts[1]);
        var on = useStatic
          ? captions[i].hasAttribute("data-static")
          : (p >= a && p <= b);
        captions[i].classList.toggle("is-active", on);
      }
      if (scrollHint) {
        scrollHint.classList.toggle("is-gone", p > 0.08 || useStatic);
      }
    }

    function setStaticHero() {
      media.classList.add("is-static", "is-ready");
      media.setAttribute("data-state", "static");
      if (POSTER_URL) still.src = POSTER_URL;
      updateCaptions(0);
      if (loadRing) loadRing.classList.remove("is-on");
      clearFixedPinStyles();
    }

    function markCompleteWithoutVideo() {
      media.classList.add("is-static", "is-ready");
      media.setAttribute("data-state", "fallback");
      if (ENDING_POSTER) still.src = ENDING_POSTER;
      updateCaptions(1);
      if (loadRing) loadRing.classList.remove("is-on");
    }

    function flushSeek() {
      if (seeking || pendingTime == null || !ready) return;
      var t = pendingTime;
      pendingTime = null;
      if (!isFinite(t)) return;
      t = clamp(t, 0, Math.max(0, duration - 0.05));
      var now = performance.now();
      if (seekMinMs > 0 && lastSeekAt && now - lastSeekAt < seekMinMs) {
        pendingTime = t;
        if (!rafId) rafId = requestAnimationFrame(tick);
        return;
      }
      var cur = video.currentTime;
      if (Math.abs(cur - t) < seekEps) {
        displayTime = t;
        /* Already on target — blit only if not mid-seek and we have dimensions */
        if (useCanvas && !seeking) blitFrame();
        return;
      }
      seeking = true;
      lastSeekAt = now;
      try {
        video.currentTime = t;
      } catch (e) {
        seeking = false;
      }
    }

    function onSeeked() {
      seeking = false;
      /* Blit ONLY on seeked — never during seeking */
      if (useCanvas) blitFrame();
      /* Do NOT snap displayTime = video.currentTime — that rubber-bands vs lerp target */
      if (pendingTime != null) flushSeek();
    }

    function requestSeek(t) {
      pendingTime = t;
      flushSeek();
    }

    function tick() {
      rafId = 0;
      if (!ready || useStatic) return;

      if (useFixedPin) updateFixedPin();

      var p = scrollProgress();
      var shaped = easePlateau(p);
      targetProgress = shaped;
      updateCaptions(p);

      var rect = pin.getBoundingClientRect();
      var offscreen = rect.bottom < 0 || rect.top > layoutVh + 80;
      if (offscreen) {
        idle = true;
        return;
      }

      if (!duration || !isFinite(duration) || duration <= 0) {
        idle = true;
        return;
      }

      var targetTime = shaped * Math.max(0, duration - 0.05);
      var delta = targetTime - displayTime;

      if (Math.abs(delta) < convergedEps) {
        displayTime = targetTime;
        if (!seeking) {
          var vs = displayTime - video.currentTime;
          if (Math.abs(vs) >= seekEps) requestSeek(displayTime);
        }
        idle = pendingTime == null && !seeking;
        if (!idle) rafId = requestAnimationFrame(tick);
        return;
      }

      idle = false;
      /* Mobile: slightly snappier converge so denser seeks feel continuous (desktop-like) */
      var lerp;
      if (Math.abs(delta) > 0.4) lerp = 0.36;
      else if (Math.abs(delta) > 0.15) lerp = isMobile ? 0.26 : 0.2;
      else lerp = isMobile ? 0.18 : 0.16;
      displayTime += delta * lerp;

      if (!seeking) {
        var vsVideo = displayTime - video.currentTime;
        if (Math.abs(vsVideo) >= seekEps) {
          requestSeek(displayTime);
        }
      } else {
        /* Single seek in flight; queue latest target only */
        pendingTime = displayTime;
      }

      if (pendingTime != null && !seeking) flushSeek();

      rafId = requestAnimationFrame(tick);
    }

    function kick() {
      if (useStatic || !ready) return;
      if (!rafId) rafId = requestAnimationFrame(tick);
    }

    function onScroll() {
      if (useStatic) return;
      if (useFixedPin) updateFixedPin();
      idle = false;
      kick();
    }

    async function fetchAsBlob(url, expectedBytes) {
      if (loadRing) loadRing.classList.add("is-on");

      var res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) throw new Error("video fetch " + res.status);

      var total = expectedBytes || Number(res.headers.get("Content-Length")) || 0;
      var reader = res.body && res.body.getReader ? res.body.getReader() : null;

      if (!reader || total <= 0) {
        var blob = await res.blob();
        if (loadRing) loadRing.classList.remove("is-on");
        return blob;
      }

      var chunks = [];
      var received = 0;
      var showRing = total > STREAM_THRESHOLD || expectedBytes > STREAM_THRESHOLD;

      if (!showRing && loadRing) {
        loadRing.classList.add("is-on");
      }

      while (true) {
        var step = await reader.read();
        if (step.done) break;
        chunks.push(step.value);
        received += step.value.length;
      }

      var assembled = new Blob(chunks, { type: "video/mp4" });
      if (loadRing) loadRing.classList.remove("is-on");
      return assembled;
    }

    async function initScrub() {
      if (useStatic) {
        setStaticHero();
        return;
      }

      if (!VIDEO_URL) {
        markCompleteWithoutVideo();
        return;
      }

      try {
        applyStagePx();
        if (useFixedPin) updateFixedPin();
        if (useCanvas) ensureCanvas();

        var blob = await fetchAsBlob(VIDEO_URL, VIDEO_BYTES);
        var objUrl = URL.createObjectURL(blob);
        video.src = objUrl;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.preload = "auto";
        video.addEventListener("seeked", onSeeked);

        await new Promise(function (resolve, reject) {
          var onMeta = function () {
            duration = video.duration || 0;
            video.removeEventListener("loadedmetadata", onMeta);
            resolve();
          };
          video.addEventListener("loadedmetadata", onMeta);
          video.addEventListener("error", function () { reject(new Error("video error")); }, { once: true });
          video.load();
        });

        try { video.currentTime = 0.001; } catch (e) {}

        /* Wait for first decoded frame before first blit */
        await new Promise(function (resolve) {
          var done = false;
          var finish = function () {
            if (done) return;
            done = true;
            video.removeEventListener("seeked", finish);
            video.removeEventListener("loadeddata", finish);
            resolve();
          };
          video.addEventListener("seeked", finish);
          video.addEventListener("loadeddata", finish);
          setTimeout(finish, 500);
        });

        ready = true;
        displayTime = 0;
        /* First blit — is-ready only after successful DOUBLE_BUFFER_BLIT */
        if (useCanvas) {
          resizeCanvas();
          seeking = false;
          blitFrame();
          if (!hasGoodFrame) {
            /* Keep poster visible; try again shortly */
            setTimeout(function () {
              seeking = false;
              blitFrame();
            }, 120);
          }
        } else {
          media.classList.add("is-ready");
          media.setAttribute("data-state", "ready");
        }

        updateCaptions(scrollProgress());
        if (useFixedPin) updateFixedPin();
        kick();
      } catch (err) {
        console.warn("Charis 10K scrub fallback:", err);
        markCompleteWithoutVideo();
      }
    }

    var mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    function recomputeMode() {
      var next = mqMotion.matches;
      if (next !== useStatic) {
        useStatic = next;
        if (useStatic) {
          if (rafId) cancelAnimationFrame(rafId);
          rafId = 0;
          seeking = false;
          pendingTime = null;
          setStaticHero();
        } else if (!ready) {
          media.classList.remove("is-static");
          initScrub();
        } else {
          media.classList.remove("is-static");
          if (hasGoodFrame || !useCanvas) media.classList.add("is-ready");
          kick();
        }
      }
    }
    if (mqMotion.addEventListener) {
      mqMotion.addEventListener("change", recomputeMode);
    } else if (mqMotion.addListener) {
      mqMotion.addListener(recomputeMode);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", function () {
      var wasMobile = isMobile;
      isMobile = window.matchMedia("(max-width: 768px)").matches;
      useCanvas = isMobile && !reduceMotion && !useStatic;
      useFixedPin = isMobile && !reduceMotion && !useStatic;
      convergedEps = isMobile ? 0.028 : 0.012;
      seekEps = isMobile ? 0.09 : 0.04;
      seekMinMs = isMobile ? 52 : 0;

      if (wasMobile && !isMobile) {
        clearFixedPinStyles();
      }

      /* Only reméasure vh on real layout/orientation change — not chrome toggle */
      var next = measureVh();
      if (Math.abs(next - layoutVh) > 100) {
        layoutVh = next;
        applyStagePx();
        pinMode = ""; /* force re-apply pin mode with new height */
      }

      if (useFixedPin) updateFixedPin();
      if (useCanvas && canvas) resizeCanvas();
      updateCaptions(scrollProgress());
      kick();
    }, { passive: true });

    window.addEventListener("orientationchange", function () {
      setTimeout(function () {
        layoutVh = measureVh();
        pinMode = "";
        applyStagePx();
        if (useFixedPin) updateFixedPin();
        if (useCanvas && canvas) resizeCanvas();
        kick();
      }, 200);
    }, { passive: true });

    initScrub();
  }

  function boot() {
    var roots = document.querySelectorAll("[data-c10k-scrub]");
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector("[data-c10k-scrub]")
      : null;
    if (root) {
      root.removeAttribute("data-c10k-init");
      initRoot(root);
    }
  });
})();
